import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ModaleLocale from "../../../common/CustomModal/ModaleLocale";
import ChoixCartes from "./ChoixCartes";
import { ACCOMPAGNEMENTS, dansLeTerritoire, erreursOrientation, optionsOrientation } from "./accompagnement";
import { orienterVersOperateur } from "./sousOrientationApi";

// ============================================================
// Modale « Orienter vers un opérateur » (CE-1414, maquette 12) : l'agent Caf
// choisit l'accompagnement, puis un opérateur que l'API propose pour cet
// accompagnement ; sans motif. Les erreurs de l'écran et de l'API s'affichent
// sous leur champ, un refus sans champ en bandeau.
// ============================================================
const ModaleOrientation = ({ allocataireId, nomAllocataire, orientation, onFermer, onOrientee }) => {
  const [accompagnement, setAccompagnement] = useState(null);
  const [operateur, setOperateur] = useState(null);
  const [erreurs, setErreurs] = useState({});
  const [refus, setRefus] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const refAccompagnement = useRef(null);
  const refOperateurs = useRef(null);
  // Le dossier peut être fermé (navigation) pendant l'enregistrement.
  const monte = useRef(true);
  useEffect(
    () => () => {
      monte.current = false;
    },
    []
  );
  const options = optionsOrientation(orientation, accompagnement);
  const choisi = ACCOMPAGNEMENTS.find((a) => a.valeur === accompagnement);
  const vide = choisi
    ? `Aucun opérateur ne propose l'${choisi.titre.toLowerCase()} avec des mesures restantes ${dansLeTerritoire(orientation.territoire)}.`
    : "Choisissez d'abord l'accompagnement : seuls les opérateurs qui le proposent et dont le quota n'est pas atteint sont listés.";

  const premierChoix = (ref) => {
    const premier = ref.current && ref.current.querySelector("input:not([disabled])");
    if (premier) premier.focus();
  };

  const orienter = () => {
    const ecran = erreursOrientation({ accompagnement, operateur });
    setErreurs(ecran);
    setRefus(null);
    if (Object.keys(ecran).length > 0) {
      premierChoix(ecran.accompagnement ? refAccompagnement : refOperateurs);
      return;
    }
    setEnCours(true);
    orienterVersOperateur(allocataireId, { operateurId: operateur, accompagnement }).then(
      (relue) => {
        const vise = orientation.choix.find((c) => c.operateur.id === operateur);
        toast.success(`Orientation vers ${vise ? vise.operateur.nom : "l'opérateur"} enregistrée.`);
        onOrientee(relue);
      },
      (err) => {
        if (!monte.current) return;
        setEnCours(false);
        if (err && err.champs) {
          setErreurs({ accompagnement: err.champs.accompagnement, operateur: err.champs.operateurId });
        } else {
          setRefus((err && err.message) || "L'orientation n'a pas pu être enregistrée.");
        }
      }
    );
  };

  const pied = (
    <div className="correction-so__pied">
      <button type="button" className="btn btn-ghost" onClick={onFermer} disabled={enCours}>
        Annuler
      </button>
      <button type="button" className="btn btn-primary" onClick={orienter} disabled={enCours}>
        Orienter vers l'opérateur
      </button>
    </div>
  );

  return (
    <ModaleLocale
      ouvert
      onFermer={onFermer}
      titre="Orienter vers un opérateur"
      sousTitre={nomAllocataire}
      taille="etroite"
      idTitre="modale-orientation-titre"
      peutFermer={!enCours}
      pied={pied}
    >
      <div className="correction-so">
        {refus && (
          <div className="alert alert-danger" role="alert">
            {refus}
          </div>
        )}
        <ChoixCartes
          nom="accompagnement"
          legende="Accompagnement"
          options={ACCOMPAGNEMENTS}
          valeur={accompagnement}
          onChoisir={(valeur) => {
            setAccompagnement(valeur);
            setOperateur(null);
            setErreurs((e) => ({ ...e, accompagnement: undefined }));
          }}
          idErreur="erreur-orientation-accompagnement"
          erreur={erreurs.accompagnement}
          refGroupe={refAccompagnement}
        />
        <ChoixCartes
          nom="orientation-operateur"
          legende="Opérateur"
          options={options}
          valeur={operateur}
          onChoisir={(valeur) => {
            setOperateur(valeur);
            setErreurs((e) => ({ ...e, operateur: undefined }));
          }}
          idErreur="erreur-orientation-operateur"
          erreur={erreurs.operateur}
          refGroupe={refOperateurs}
          vide={vide}
        />
        <p className="correction-so__aide">
          Une mesure est décomptée du quota FSE+ de l'opérateur dès l'orientation. L'orientation et votre nom restent
          dans l'historique de l'allocataire.
        </p>
      </div>
    </ModaleLocale>
  );
};

export default ModaleOrientation;
